import type {
  BrandSpecial,
  MaterializedMenuProduct,
  StoreSpecial as V3StoreSpecial,
} from '@iheartjane/data-access';
import { PageContextEnum, Placement } from '@iheartjane/dm-sdk';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import type { Nullable, QualifyingStatusEnum } from '@iheartjane/types';
import { useEffect, useMemo } from 'react';

import type { UseFetchPlacement } from '@jane/dm/internal';
import { useFetchCustomMultiPlacements } from '@jane/dm/internal';
import { searchOptions } from '@jane/kiosk/data-access';
import { useBrandSpecials } from '@jane/kiosk/providers/lib/BrandSpecialsProvider/BrandSpecialsProvider';
import { useSearchContext } from '@jane/kiosk/providers/lib/SearchProvider';
import type { MenuCategory } from '@jane/kiosk/util';
import { trackSearchedMenuProducts } from '@jane/kiosk/util';
import {
  buildSearchFilter,
  parseKindFromStaticFilters,
} from '@jane/search/util';
/* eslint-disable-next-line no-restricted-imports */
import type { StoreSpecial } from '@jane/shared/models';
import type { Store, StoreSpecial as ZStoreSpecial } from '@jane/shared/types';

import { useGoldFacetsAndFilters } from './useGoldFacetsAndFilters';
import { useProductSort } from './useProductSort';

const DEFAULT_SEARCH_FACETS = [
  'activities',
  'aggregate_rating',
  'applicable_special_ids',
  'available_weights',
  'brand_subtype',
  'brand',
  'bucket_price',
  'category',
  'feelings',
  'has_brand_discount',
  'kind',
  'percent_cbd',
  'percent_thc',
  'root_types',
  'compound_names',
];

interface MenuProductSearchArgs {
  brandSpecial?: Nullable<BrandSpecial>;
  category: MenuCategory;
  columnCount: number;
  defaultSortName: Nullable<string>;
  enableMidGridRow?: boolean;
  featuredCategory?: string;
  isEnabled?: boolean;
  /**
   * The relevant QualifyingStatus if a store special is in scope.
   */
  qualifyingStatus?: Nullable<QualifyingStatusEnum>;
  store: Store;
  storeSpecial?: Nullable<StoreSpecial | ZStoreSpecial | V3StoreSpecial>;
}

/**
 * A hook that provides a shared way to perform the same infinite AlgoliaProduct search
 * so that results stay synchronized between a menu's filters and the grid of products.
 */
export const useMenuProductSearch = ({
  brandSpecial,
  category,
  defaultSortName,
  enableMidGridRow = false,
  featuredCategory,
  qualifyingStatus,
  store,
  storeSpecial,
  isEnabled = true,
  columnCount,
}: MenuProductSearchArgs): UseFetchPlacement<MaterializedMenuProduct> => {
  const { searchState } = useSearchContext();
  const midGridAdRow = useFlag(FLAGS.c3MidGridAdRow);
  const { brandSpecialFilter } = useBrandSpecials();
  const { optionalFiltersString, searchSort } = useProductSort({
    defaultSortName,
    optionalFilters: store?.optional_filters,
    store,
  });

  const isGold = category === 'jane-gold';

  const options = searchOptions({
    brandSpecial,
    categories: [category],
    customRowFilter: isGold ? brandSpecialFilter : featuredCategory,
    optionalFilters: optionalFiltersString,
    qualifyingStatus,
    storeId: store?.id,
    storeSpecial,
  });

  const searchFilter = buildSearchFilter({
    searchState,
    staticFilters: options.staticFilters,
  });

  const { goldFacetsSearchFilters } = useGoldFacetsAndFilters({
    goldFacetsEnabled: false,
    staticFilters: options.staticFilters,
  });

  const goldFilterValues = searchState.filters?.['has_brand_discount'] ?? [];
  const isFilteredByGold = goldFilterValues?.length > 0;

  const filterContext = useMemo(
    () =>
      enableMidGridRow
        ? parseKindFromStaticFilters(options.staticFilters)
        : undefined,
    [options.staticFilters, enableMidGridRow]
  );

  const { placementViewModels, isLoading, isError, isSuccess } =
    useFetchCustomMultiPlacements<MaterializedMenuProduct>({
      storeId: Number(store?.id),
      numColumns: columnCount,
      placements: [
        {
          placement: Placement.MenuInlineTable,
          searchFilter: isFilteredByGold
            ? goldFacetsSearchFilters
            : searchFilter,
          searchSort,
          searchFacets: DEFAULT_SEARCH_FACETS,
        },
        ...(midGridAdRow && enableMidGridRow
          ? [
              {
                placement: Placement.MenuDynamicRow as const,
                filterContext,
                pageContext: PageContextEnum.Midgrid,
              },
            ]
          : []),
      ],
      enabled: isEnabled,
    });

  const { isPlacement, fetchNextPage, hasNextPage, placement } =
    placementViewModels[0] ?? {};

  const { isPlacement: isDynamicPlacement, placement: dynamicPlacement } =
    placementViewModels?.length > 1 ? placementViewModels[1] : {};

  const adaptedResponse: UseFetchPlacement<MaterializedMenuProduct> = useMemo(
    () => ({
      dynamicPlacement:
        isDynamicPlacement && dynamicPlacement ? dynamicPlacement : undefined,
      fetchNextPage,
      hasNextPage,
      instance: isPlacement ? placement : undefined,
      isError,
      isLoading,
      isSuccess,
      numHits: isPlacement ? (placement?.totalProducts ?? 0) : 0,
      products: isPlacement ? (placement?.products ?? []) : [],
      searchResultFacets: isPlacement ? (placement?.searchFacets ?? {}) : {},
    }),
    [
      isDynamicPlacement,
      dynamicPlacement,
      isPlacement,
      fetchNextPage,
      hasNextPage,
      placement,
      isError,
      isLoading,
      isSuccess,
    ]
  );

  useEffect(() => {
    trackSearchedMenuProducts();
  }, [adaptedResponse.products]);

  return adaptedResponse;
};
